import { toast } from "$global-providers/toast-provider";
import { gql } from "@apollo/client";
import { LineItemSupplier } from "elephant-web/gql";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import useCurrencyFormatter from "../../../hooks/useCurrencyFormatter";
import type { InvoiceCardDetailsInfo, LineItem, Payers } from "../types";
import { useInvoiceQuery } from "./__generated__/useFetchInvoice.gql";

gql`
  query invoice($invoiceId: String!) {
    invoice(invoiceId: $invoiceId) {
      _id
      invoiceStatus
      patientType
      created
      patient {
        insurance {
          insuranceType
          insuranceDetails
        }
      }
      balance {
        total
        paid
        due
        currency
      }
      lineItems {
        id
        quantity
        cost
        automated
        disabled
        incurredAt
        priceListItem {
          uuid
          name
          priceListItemType
          cost
          payers {
            tariffId
            name
            amountInPence
            revenueDepartment
          }
          activeTariffId
          revenueDepartment
        }
        externalReference {
          origin
          foreignId
        }
        supplier
        missingPrice
      }
      payers {
        type
        name
        balance {
          total
          paid
          due
          currency
          waived
          exempt
        }
        insuranceDetails {
          name
          insuranceNumber
          nationalIdNumber
          dependent
        }
      }
    }
  }
`;

const getActiveTariffCost = (
  activeTariffId: string | null | undefined,
  payers: Payers[],
): number => {
  if (!activeTariffId) return 0;
  return payers.find((p) => p.tariffId === activeTariffId)?.amountInPence ?? 0;
};

export function useFetchInvoice(invoiceId: string) {
  const { formatMessage } = useIntl();
  const { currencyFormatter } = useCurrencyFormatter();

  const { loading, error, data } = useInvoiceQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      invoiceId,
    },
    onError() {
      toast.error(
        formatMessage({
          id: "invoicing.getInvoice.error.msg",
          defaultMessage: "Failed to fetch this invoice",
        }),
      );
    },
  });

  const invoice = useMemo(() => {
    if (loading || error) {
      return null;
    }
    const {
      invoice: {
        balance,
        lineItems,
        invoiceStatus,
        patientType,
        payers,
        patient,
        created: invoiceCreated,
      },
    } = data;
    // @ts-expect-error: Switching to generated query has shown these original types were broken
    const transformedLineItems = lineItems.map<LineItem>((lineItem) => {
      return {
        id: lineItem.id,
        formattedPrice: lineItem.missingPrice
          ? currencyFormatter(0)
          : currencyFormatter(
              getActiveTariffCost(
                lineItem.priceListItem.activeTariffId,
                // @ts-expect-error: Switching to generated query has shown these original types were broken
                lineItem.priceListItem.payers,
              ),
            ),
        cost: lineItem.cost,
        quantity: lineItem.quantity,
        priceListItem: {
          ...lineItem.priceListItem,
          externalReference: lineItem.externalReference,
        },
        automated: lineItem.automated,
        disabled: lineItem.disabled,
        missingPrice: lineItem.missingPrice ?? false,
        // Default a null supplier (auto-generated rows) to GOVERNMENT so the
        // required supplier field is valid; the cashier can change it.
        supplier: lineItem.supplier ?? LineItemSupplier.Government,
        // Invoice creation used as a fallback for those created before line items were individualised
        incurredAt: lineItem.incurredAt
          ? new Date(lineItem.incurredAt)
          : new Date(invoiceCreated),
      };
    });

    const transformedInvoice: InvoiceCardDetailsInfo = {
      invoiceStatus,
      patientType,
      currencyCode: balance?.currency,
      lineItems: transformedLineItems,
      total: balance.total,
      paymentIsDue: balance.due !== 0,
      // @ts-expect-error: Switching to generated query has shown these original types were broken
      payers,
      // @ts-expect-error: Switching to generated query has shown these original types were broken
      patient,
      hasPatientPayer: !!payers.find(
        (p) => p.type === "PATIENT" && p.balance.paid > 0,
      ),
    };
    return transformedInvoice;
    // no exhaustive deps or it infinite re-renders
  }, [loading, error, data, currencyFormatter]);

  return { loading, error, invoice };
}
